/**
 * Deprecation warning: It is recommended to use context instead of these
 * "pseudo" selectors for accessing query information.
 *
 * In particular, `app/components/router/createParamContext` is a helper
 * designed for this purpose and should be used instead.
 */

import { DictionaryTagOptions } from 'app/components/editor/wordList/DictionaryTagSelector';
import {
  CATEGORY_POSITION_ASCENDING,
  COURSE_POSITION_ASCENDING,
  EDITOR_CATEGORIES_ORDERS,
  EDITOR_COURSES_ORDERS,
  EDITOR_LESSONS_ORDERS,
  LESSON_BOOKING_ORDERS,
  RELEVANCE,
  USER_ORDERS,
} from 'app/constants/EditorSortOrders';
import { StatusFilterSearchQuery } from 'app/constants/LessonStatus';
import { QueryParameter } from 'app/constants/QueryParameter';
import { UserFilterQuery } from 'app/constants/UserStatus';
import { WordOptions } from 'app/constants/WordOptions';
import { BookingQueryParam } from 'app/models/rtc/booking/constants';
import { createSelector } from 'app/utils/reselect-legacy';
import { List, Map } from 'immutable';
import { parse, stringify } from 'query-string';

const getOrder = history => parseString(getQuery(history).order);

/**
 * This function generates the functions `getOrder` and `getOrderOptions` that
 * given a history object, will return the currently selected order and the
 * list of orders you can select from. This is meant to be primarily used with
 * `withSearchListLoader` and `SortOrderSelector`.
 *
 * If searching with a text query, an additional RELEVANCE option is added as
 * the first entry in the list of options. If the order in history is not
 * specified or is not in the list of options, it will default to the first
 * ordering.
 *
 * `baseOptions` is the list of orderings to be used. `withQueryOptions` can
 * also be provided to have different options if a text query is present. This
 * should not contain RELEVANCE as it will still be added by this function.
 *
 * @param {List<SortOrder>} baseOptions
 * @param {List<SortOrder>} [withQueryOptions]
 */
const makeOrderWithRelevance = (baseOptions, withQueryOptions) => {
  const optionsWithRelevance = List([
    RELEVANCE,
    ...(withQueryOptions || baseOptions),
  ]);

  const getOrderOptions = (history) =>
    (hasSearchQuery(history) ? optionsWithRelevance : baseOptions);

  const getOrderWithRelevance = (history) => {
    const order = getOrder(history);
    const options = getOrderOptions(history);
    if (order && options.find(({ id }) => id === order)) {
      return order;
    } else {
      return options.first().id;
    }
  };

  return {
    getOrder: getOrderWithRelevance,
    getOrderOptions,
  };
};

// Don't trim the query because it may contain spaces.
export const getSearchQuery = history =>
  parseStringWithoutTrim(getQuery(history).query) || '';

export const hasSearchQuery = history => Boolean(getSearchQuery(history));

export const getRedirectTo = history => (
  // Pass along existing redirectTo param, so login link on login
  // page will point to the same place
  getQuery(history).redirectTo ||
  // Pass current location as redirectTo
  history.createHref(history.location)
);

export const getTags = (history) => parseArray(getQuery(history).tags);
export const getInternalTags = history => parseArray(getQuery(history).internal_tags);

/**
 * @param {Router} history
 * @returns {StatusFilterSearchQuery}
 */
export const getLessonStatus = history => {
  const statusFilterSearchQuery = new StatusFilterSearchQuery();
  const filters = getSearchFilters(history, statusFilterSearchQuery.keySeq());
  return statusFilterSearchQuery.merge(filters);
};

export const getWordOptions = history => {
  const wordOptions = new WordOptions();
  const filters = getSearchFilters(history, wordOptions.keySeq());
  return wordOptions.merge(filters);
};

export const getDictionaryTagOptions = history => {
  const dictionaryOptions = new DictionaryTagOptions();
  const filters = getSearchFilters(history, dictionaryOptions.keySeq());
  return dictionaryOptions.merge(filters);
};

/**
 * @param {Router} history
 * @returns {UserFilterQuery}
 */
export const getUserStatus = history => {
  const userSearchQuery = new UserFilterQuery();
  const filters = getSearchFilters(history, userSearchQuery.keySeq());
  return userSearchQuery.merge(filters);
};

export const getBrands = (history) => parseArray(getQuery(history).brands);

// Lesson list order
export const {
  getOrder: getLessonOrder,
  getOrderOptions: getLessonOrderOptions,
} = makeOrderWithRelevance(EDITOR_LESSONS_ORDERS);

// Category list order
export const {
  getOrder: getCategoryOrder,
  getOrderOptions: getCategoryOrderOptions,
} = makeOrderWithRelevance(EDITOR_CATEGORIES_ORDERS);

// Course list order
export const {
  getOrder: getCourseOrder,
  getOrderOptions: getCourseOrderOptions,
} = makeOrderWithRelevance(EDITOR_COURSES_ORDERS);

// Category editor order
export const {
  getOrder: getCategoryCourseOrder,
  getOrderOptions: getCategoryCourseOrderOptions,
} = makeOrderWithRelevance(
  EDITOR_COURSES_ORDERS.insert(0, CATEGORY_POSITION_ASCENDING),
  EDITOR_COURSES_ORDERS,
);

// Course editor order
export const {
  getOrder: getCourseLessonOrder,
  getOrderOptions: getCourseLessonOrderOptions,
} = makeOrderWithRelevance(
  EDITOR_LESSONS_ORDERS.insert(0, COURSE_POSITION_ASCENDING),
  EDITOR_LESSONS_ORDERS,
);

// User
export const {
  getOrder: getUserOrder,
  getOrderOptions: getUserOrderOptions,
} = makeOrderWithRelevance(USER_ORDERS);

// Staff
export const getStaffTeam = history =>
  parseString(getQuery(history).staff_team);

// Lesson Bookings
export const {
  getOrder: getLessonBookingOrder,
  getOrderOptions: getLessonBookingOrderOptions,
} = makeOrderWithRelevance(LESSON_BOOKING_ORDERS);

/**
 * Merges in bookingId and bookingType with the current history parameters.
 *
 * @param {*} history
 * @param {string} bookingId
 * @param {string} bookingType
 * @returns {string}
 */
export const getBookingPath = (history, bookingId, bookingType) => {
  const search = stringify({
    ...getQuery(history),
    [BookingQueryParam.BookingId]: bookingId,
    [BookingQueryParam.BookingType]: bookingType,
  });

  return history.createHref({ ...history.location, search });
};

// Global Language Query Parameter
export const getLanguageQueryParam = history =>
  parseString(getQuery(history)[QueryParameter.Language]);

// Selector helpers

export const getQuery = createSelector(
  [(history) => history.location.search],
  (search) => parse(search),
);

/**
 * @param {Router} history
 * @param {List|Array} filters      param queries that act as filters
 * @returns {Map}
 */
const getSearchFilters = (history, filters = []) => {
  const values = filters.map(filter => [filter, parseBoolean(getQuery(history)[filter])])
                        // Filter out values that are undefined.
                        .filter(([id, value]) => value !== undefined);
  return new Map(values);
};

const parseArrayWithDefault = (value, mapValues = (x => x), fallbackValue) => {
  const arrayForSure = value instanceof Array ? value : [value];
  const values = arrayForSure.filter(x => x !== undefined);

  if (values.length > 0) {
    return new List(values.map(mapValues));
  } else {
    return fallbackValue;
  }
};

const parseArray = (value, mapValues) => parseArrayWithDefault(value, mapValues, new List());

const parseString = value => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return undefined;
  } else {
    return trimmed;
  }
};

const parseStringWithoutTrim = value => (
  (typeof value !== 'string' || value.length === 0) ? undefined : value
);

const parseBoolean = value => {
  const asString = parseString(value);

  if (asString === 'true') {
    return true;
  } else if (asString === 'false') {
    return false;
  } else {
    return undefined;
  }
};
